import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, GripVertical } from 'lucide-react';
import { db, GoalTemplate, MatterTemplate } from '../../lib/db';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface CreateProjectTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  goalTemplates: GoalTemplate[];
  matterTemplates: MatterTemplate[];
  onSuccess: () => void;
}

interface SelectedItem {
  id: string;
  type: 'matter' | 'deliverable';
  templateId: string;
  position: number;
}

function SortableItem({ 
  item, 
  template,
  onRemove 
}: { 
  item: SelectedItem;
  template: GoalTemplate | MatterTemplate | undefined;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (!template) return null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
    >
      <div {...attributes} {...listeners} className="cursor-grab">
        <GripVertical className="h-4 w-4 text-gray-400" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium px-2 py-1 rounded ${
            item.type === 'matter' 
              ? 'bg-purple-100 text-purple-700' 
              : 'bg-blue-100 text-blue-700'
          }`}>
            {item.type === 'matter' ? 'Matter' : 'Deliverable'}
          </span>
          <p className="font-medium text-gray-900">{template.title}</p>
        </div>
        {template.description && (
          <p className="text-sm text-gray-600 mt-1">{template.description}</p>
        )}
      </div>
      <button
        onClick={onRemove}
        className="text-red-600 hover:text-red-700"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export function CreateProjectTemplateModal({
  isOpen,
  onClose,
  userId,
  goalTemplates,
  matterTemplates,
  onSuccess,
}: CreateProjectTemplateModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setDescription('');
      setSelectedItems([]);
    }
  }, [isOpen]);

  const handleAddMatter = (matterTemplateId: string) => {
    const newItem: SelectedItem = {
      id: `temp-${Date.now()}-${Math.random()}`,
      type: 'matter',
      templateId: matterTemplateId,
      position: selectedItems.length,
    };
    setSelectedItems([...selectedItems, newItem]);
  };

  const handleAddDeliverable = (goalTemplateId: string) => {
    const newItem: SelectedItem = {
      id: `temp-${Date.now()}-${Math.random()}`,
      type: 'deliverable',
      templateId: goalTemplateId,
      position: selectedItems.length,
    };
    setSelectedItems([...selectedItems, newItem]);
  };

  const handleRemoveItem = (id: string) => {
    setSelectedItems(selectedItems.filter(item => item.id !== id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedItems.findIndex(item => item.id === active.id);
      const newIndex = selectedItems.findIndex(item => item.id === over.id);
      
      const newItems = [...selectedItems];
      const [removed] = newItems.splice(oldIndex, 1);
      newItems.splice(newIndex, 0, removed);
      
      // Update positions
      newItems.forEach((item, index) => {
        item.position = index;
      });
      
      setSelectedItems(newItems);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedItems.length === 0) {
      alert('Please select at least one matter or deliverable template');
      return;
    }

    setSaving(true);
    try {
      // Create the project template
      const projectTemplate = await db.projectTemplates.create({
        user_id: userId,
        title,
        description: description || undefined,
        is_system: false,
        position: 0,
      });

      // Create associations for matters and deliverables
      for (const item of selectedItems) {
        if (item.type === 'matter') {
          await db.projectTemplateMatters.create({
            project_template_id: projectTemplate.id,
            matter_template_id: item.templateId,
            position: item.position,
          });
        } else {
          await db.projectTemplateDeliverables.create({
            project_template_id: projectTemplate.id,
            goal_template_id: item.templateId,
            position: item.position,
          });
        }
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating project template:', error);
      alert('Failed to create project template');
    } finally {
      setSaving(false);
    }
  };

  const availableMatterTemplates = matterTemplates.filter(
    template => !selectedItems.some(item => item.type === 'matter' && item.templateId === template.id)
  );

  const availableGoalTemplates = goalTemplates.filter(
    template => !selectedItems.some(item => item.type === 'deliverable' && item.templateId === template.id)
  );

  const getTemplate = (item: SelectedItem) => {
    if (item.type === 'matter') {
      return matterTemplates.find(t => t.id === item.templateId);
    } else {
      return goalTemplates.find(t => t.id === item.templateId);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      
      <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 max-w-2xl mx-auto bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Create Project Template</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Project Template Title
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Matters and Deliverables
                </label>
                <div className="flex gap-2">
                  {availableMatterTemplates.length > 0 && (
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddMatter(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="pl-3 pr-8 py-1 text-sm border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      defaultValue=""
                    >
                      <option value="" disabled>Add matter...</option>
                      {availableMatterTemplates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.title}
                        </option>
                      ))}
                    </select>
                  )}
                  {availableGoalTemplates.length > 0 && (
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddDeliverable(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="pl-3 pr-8 py-1 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      defaultValue=""
                    >
                      <option value="" disabled>Add deliverable...</option>
                      {availableGoalTemplates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.title}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {selectedItems.length === 0 ? (
                <p className="text-sm text-gray-500 italic">
                  No items selected. Add at least one matter or deliverable template.
                </p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={selectedItems.map(item => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {selectedItems.map(item => (
                        <SortableItem
                          key={item.id}
                          item={item}
                          template={getTemplate(item)}
                          onRemove={() => handleRemoveItem(item.id)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={saving || selectedItems.length === 0}
            >
              {saving ? 'Creating...' : 'Create Project Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}